/**
 * Firebase app + Auth singletons for the web frontend.
 *
 * The web config is non-secret and injected at build time via webpack's
 * DefinePlugin (see `frontend/webpack/webpack.common.js`). The backend verifies
 * the ID tokens this client mints, so the only sensitive material (the service
 * account) lives server-side.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInAnonymously,
  type Auth,
  type User,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: (process.env.FIREBASE_API_KEY as string) || '',
  authDomain: (process.env.FIREBASE_AUTH_DOMAIN as string) || '',
  projectId: (process.env.FIREBASE_PROJECT_ID as string) || '',
  storageBucket: (process.env.FIREBASE_STORAGE_BUCKET as string) || '',
  messagingSenderId: (process.env.FIREBASE_MESSAGING_SENDER_ID as string) || '',
  appId: (process.env.FIREBASE_APP_ID as string) || '',
};

if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  // Fail loudly in dev; a missing config otherwise surfaces as opaque
  // auth/invalid-api-key errors at the first sign-in attempt.
  console.error(
    'Missing Firebase web config. Set FIREBASE_API_KEY / FIREBASE_PROJECT_ID ' +
      '(and friends) in the repo-root .env (see .env.example; injected via ' +
      'webpack DefinePlugin).'
  );
}

export const firebaseApp: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(firebaseApp);

/** Google sign-in provider (shared by the social buttons). */
export const googleProvider = new GoogleAuthProvider();

/**
 * Ensure there is a signed-in Firebase user, creating a throwaway *anonymous*
 * one if nobody is signed in yet. Backs the home-page "try before you sign up"
 * guest-upload flow: the anonymous user mints a real ID token (with
 * `firebase.sign_in_provider === 'anonymous'`) that the backend verifies and
 * treats as a guest. Returns the current user (existing or freshly anonymous).
 *
 * No-ops when someone is already signed in (anonymous or real), so calling it
 * before each guest upload is safe.
 */
export async function ensureAnonymousUser(): Promise<User> {
  if (auth.currentUser) {
    return auth.currentUser;
  }
  const cred = await signInAnonymously(auth);
  return cred.user;
}
